import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useDispatch } from 'react-redux';
import allActions from '../../actions';

import moment from 'moment';

import UnoButton from '../utils/UnoButton';
import MyAutoComplete from '../utils/MyAutoComplete';
import MyDatePicker from '../utils/MyDatePicker';

import { TemporaryTransferAPI } from '../../API/temporary_transfer';

import { unoTheme, myLabelInput } from '../../assets/styles/unoTheme';

const useStyles = makeStyles((theme) => ({
    contentModal: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingBottom: theme.spacing(2),
    },
    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
        },
    },
    subtitle: {
        fontSize: '14px',
        color: unoTheme.secondaryColor,
        marginBottom: theme.spacing(2),
    },
    effectivePeriodBox: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: unoTheme.secondPrimaryButtonColor,
        border: '1px solid ' + unoTheme.borderInputColor,
        borderRadius: '4px',
    },
    effectivePeriodTitle: {
        fontSize: '14px',
        fontWeight: 500,
        color: unoTheme.mainColor,
        marginBottom: theme.spacing(1),
    },
    effectivePeriodLabel: {
        fontSize: '13px',
        color: '#000000',
    },
    effectivePeriodValue: {
        fontSize: '13px',
        color: unoTheme.defaultTextColor,
    },
    effectivePeriodReturn: {
        fontSize: '13px',
        color: unoTheme.defaultTextColor,
        fontStyle: 'italic',
        marginTop: theme.spacing(1),
    },
    disclaimer: {
        marginTop: theme.spacing(2),
        color: unoTheme.secondaryColor,
        fontStyle: 'italic',
    },
    validateMessage: {
        color: 'red',
        marginTop: theme.spacing(2),
    },
    actionsFlex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    label: myLabelInput,
}));

export default function NewTemporaryTransferModal(props) {

    const { open, handleAction, selectedClients = [], advisors = [], scheduledByClient = {} } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    const [destinationAdvisor, setDestinationAdvisor] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [validateMessage, setValidateMessage] = useState('');
    const [loader, setLoader] = useState(false);

    // Subtítulo: agrupa os clientes selecionados por consultor de origem
    const groups = {};
    selectedClients.forEach((c) => {
        const name = (c.advisor_name || 'Sem consultor').toUpperCase();
        groups[name] = (groups[name] || 0) + 1;
    });
    const subtitle = Object.entries(groups)
        .map(([name, count]) => `${count} cliente(s) selecionado(s) de ${name}`)
        .join(', ');

    // Período efetivo: espelha exatamente o que o backend vai gravar
    const startDay = startDate && moment.utc(startDate).isValid() ? moment.utc(startDate).startOf('day') : null;
    const endDay = endDate && moment.utc(endDate).isValid() ? moment.utc(endDate).startOf('day') : null;
    const showEffectivePeriod = startDay && endDay && !endDay.isBefore(startDay);
    const totalDays = showEffectivePeriod ? endDay.diff(startDay, 'days') + 1 : 0;

    function clearForm() {
        setDestinationAdvisor(null);
        setStartDate(null);
        setEndDate(null);
        setSubmitted(false);
        setValidateMessage('');
    }

    function closeModal() {
        clearForm();
        handleAction(false);
    }

    async function doScheduleTransfer() {
        setSubmitted(true);
        setValidateMessage('');

        if (!destinationAdvisor || !destinationAdvisor.id) {
            setValidateMessage('Selecione o consultor destino');
            return;
        }

        // O destino não pode ser o mesmo consultor de origem dos clientes selecionados
        const originAdvisorIds = [...new Set(selectedClients
            .map((c) => c.advisor_id)
            .filter((id) => id != null))];
        if (originAdvisorIds.includes(destinationAdvisor.id)) {
            setValidateMessage('O consultor destino deve ser diferente do consultor de origem');
            return;
        }

        if (!startDate || !moment.utc(startDate).isValid()
            || !endDate || !moment.utc(endDate).isValid()) {
            setValidateMessage('Preencha as datas de início e término');
            return;
        }

        if (moment.utc(endDate).isBefore(moment.utc(startDate), 'day')) {
            setValidateMessage('A data de término deve ser igual ou posterior à data de início');
            return;
        }

        // Mesmo predicado de sobreposição do backend (existente.início <= novo.fim
        // AND existente.fim >= novo.início), com granularidade de dia para não depender
        // da hora gravada. O backend segue sendo a autoridade — isso é só para dar a
        // mensagem inline sem ida ao servidor.
        const conflicts = selectedClients.filter((c) =>
            (scheduledByClient[c.id] || []).some((t) =>
                moment.utc(t.period_start).isSameOrBefore(endDay, 'day')
                && moment.utc(t.period_end).isSameOrAfter(startDay, 'day')));

        if (conflicts.length) {
            setValidateMessage('Período em conflito com agendamento existente: '
                + conflicts.map((c) => c.label_name).join(', '));
            return;
        }

        const payload = {
            origin_user_id: originAdvisorIds[0],
            destination_user_id: destinationAdvisor.id,
            client_ids: selectedClients.map((c) => c.id),
            period_start: moment.utc(startDate).format('DD/MM/YYYY'),
            period_end: moment.utc(endDate).format('DD/MM/YYYY'),
        };

        setLoader(true);
        const response = await TemporaryTransferAPI.createTemporaryTransfer(payload);
        setLoader(false);

        if (response.success) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'success',
                message: 'Transferência agendada com sucesso!',
            }));
            clearForm();
            // Fecha o painel lateral e sinaliza atualização para a lista da aba Gestão de férias
            dispatch(allActions.rightContentActions.update());
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: 'Erro ao agendar a transferência: ' + (response.body && response.body.message ? response.body.message : ''),
            }));
        }
    }

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'sm'}
            open={open}
            onClose={() => closeModal()}>

            <DialogTitle className={classes.dialogTitle}>
                {'Nova transferência temporária'}
            </DialogTitle>

            <DialogContent className={classes.contentModal}>

                <Typography className={classes.subtitle}>
                    {subtitle}
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <MyAutoComplete
                            id={'destination-advisor-temporary-transfer'}
                            outerlabel={'Consultor destino (temporário)'}
                            options={advisors}
                            autoComplete
                            value={destinationAdvisor ? destinationAdvisor : null}
                            onChange={(event, newValue) => setDestinationAdvisor(newValue)}
                            placeholder={'Selecione o consultor'}
                            getOptionLabel={(option) => option.name.toUpperCase()}
                            getOptionSelected={(option, value) => option.id === value.id}
                            renderOption={(option) => (
                                <React.Fragment key={'autocomplete-destination-' + option.id}>
                                    <span>{option.name.toUpperCase()}</span>
                                </React.Fragment>
                            )}
                            validator={submitted && !destinationAdvisor ? true : undefined}
                            helperText={'Selecione o consultor destino'}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MyDatePicker
                            margin="dense"
                            outerlabel="Data de início"
                            format="DD/MM/YYYY"
                            value={startDate}
                            onChange={(value) => setStartDate(value)}
                            validator={submitted && !startDate ? true : undefined}
                            helperText={'Insira uma data válida'}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MyDatePicker
                            margin="dense"
                            outerlabel="Data de término"
                            format="DD/MM/YYYY"
                            value={endDate}
                            onChange={(value) => setEndDate(value)}
                            validator={submitted && !endDate ? true : undefined}
                            helperText={'Insira uma data válida'}
                        />
                    </Grid>
                </Grid>

                {showEffectivePeriod &&
                    <div className={classes.effectivePeriodBox}>
                        <Typography className={classes.effectivePeriodTitle}>
                            {'Período efetivo da transferência'}
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={3}>
                                <Typography className={classes.effectivePeriodLabel}>{'Início'}</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                <Typography className={classes.effectivePeriodValue}>
                                    {startDay.format('DD/MM/YYYY') + ' às 00:00:00'}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography className={classes.effectivePeriodLabel}>{'Término'}</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                <Typography className={classes.effectivePeriodValue}>
                                    {endDay.format('DD/MM/YYYY') + ' às 23:59:59'}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography className={classes.effectivePeriodLabel}>{'Duração'}</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                <Typography className={classes.effectivePeriodValue}>
                                    {totalDays + (totalDays === 1 ? ' dia' : ' dias')}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Typography className={classes.effectivePeriodReturn}>
                            {'Os clientes retornam ao consultor de origem em '
                                + endDay.clone().add(1, 'day').format('DD/MM/YYYY') + '.'}
                        </Typography>
                    </div>
                }

                <Typography variant={'body2'} className={classes.disclaimer}>
                    {'Novos clientes atribuídos ao consultor original não entram nessa regra.'}
                </Typography>

                {validateMessage !== '' &&
                    <Typography variant={'body2'} className={classes.validateMessage}>
                        {validateMessage}
                    </Typography>
                }

            </DialogContent>

            <DialogActions className={classes.actionsFlex}>
                <UnoButton disabled={loader} onClick={() => closeModal()}>
                    {'Cancelar'}
                </UnoButton>
                <UnoButton
                    type="primary"
                    disabled={loader}
                    onClick={() => doScheduleTransfer()}>
                    {'Agendar transferência'}
                </UnoButton>
            </DialogActions>

        </Dialog>
    );
}
